import React from 'react'
import styled from 'styled-components'
import Plaatjie from '@ubo/plaatjie'
import ButtonLanding from './ButtonLanding'
import C3Mark from './C3Mark'

const StyledHero = styled.section`
  position: relative;
  overflow: hidden;

  @media (min-width: 992px) {
    min-height: 864px;
    padding-bottom: 3rem;
  }

  @media (max-width: 991.98px) {
    padding-bottom: 3rem;
  }
`

// Slim dark bar the logo badge hangs from.
const TopBar = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  background-color: ${(props) => props.theme.color.main};

  @media (min-width: 992px) {
    height: 45px;
  }

  @media (max-width: 991.98px) {
    height: 25px;
  }
`

const Background = styled(Plaatjie)`
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.color.main};
`

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(28, 28, 28, 0.85) 0%,
    rgba(28, 28, 28, 0.35) 45%,
    rgba(28, 28, 28, 0.75) 100%
  );
`

// Subtle red wash fading up from the bottom of the image.
const BottomFade = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(180deg, #1c1c1c10 0%, #1c1c1c50 100%);

  @media (min-width: 992px) {
    height: 240px;
  }

  @media (max-width: 991.98px) {
    height: 140px;
  }
`

const Badge = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(props) => props.theme.color.main};
  /* Notched bottom-left corner */
  clip-path: polygon(0 0, 100% 0, 100% 100%, 22% 100%, 0 79%);

  @media (min-width: 992px) {
    width: 221px;
    height: 206px;
    padding-top: 40px;
  }

  @media (max-width: 991.98px) {
    width: 130px;
    height: 120px;
    padding-top: 20px;
  }
`

const BadgeTitle = styled.span`
  display: block;
  font-family: ${(props) => props.theme.font.family.secondary};
  color: #fff;
  text-transform: uppercase;
  font-weight: 500;

  @media (min-width: 992px) {
    font-size: 22px;
    letter-spacing: 2.2px;
    margin-top: 26px;
  }

  @media (max-width: 991.98px) {
    font-size: 13px;
    letter-spacing: 1px;
    margin-top: 12px;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 992px) {
    /* The title starts at 264px on the design, of which 45px is the top bar. */
    padding-top: 219px;
  }

  @media (max-width: 991.98px) {
    padding-top: 130px;
  }
`

const TitleTop = styled.h1`
  font-family: ${(props) => props.theme.font.family.secondary};
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  text-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  margin-bottom: 0;

  @media (min-width: 1200px) {
    font-size: 104px;
    line-height: 70px;
  }

  @media (min-width: 992px) and (max-width: 1199.98px) {
    font-size: 80px;
    line-height: 55px;
  }

  @media (max-width: 991.98px) {
    font-size: 46px;
    line-height: 40px;
  }

  @media (max-width: 575.98px) {
    font-size: 32px;
    line-height: 30px;
  }
`

const TitleBottom = styled.span`
  display: block;
  font-family: ${(props) => props.theme.font.family.secondary};
  font-weight: 600;
  text-transform: uppercase;
  -webkit-text-fill-color: transparent;

  @media (min-width: 1200px) {
    font-size: 175px;
    line-height: 110px;
    -webkit-text-stroke: 2px #fff;
    margin-top: 74px;
  }

  @media (min-width: 992px) and (max-width: 1199.98px) {
    font-size: 130px;
    line-height: 100px;
    -webkit-text-stroke: 2px #fff;
    margin-top: 2rem;
  }

  @media (max-width: 991.98px) {
    font-size: 72px;
    line-height: 60px;
    -webkit-text-stroke: 1px #fff;
    margin-top: 1rem;
  }

  @media (max-width: 575.98px) {
    font-size: 46px;
    line-height: 40px;
  }
`

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  @media (min-width: 992px) {
    margin-top: 45px;
    gap: 26px;
  }

  @media (max-width: 991.98px) {
    margin-top: 2rem;
    gap: 15px;
  }
`

function HeroLanding({ title, titleOutline, image, link, secondLink }) {
  return (
    <StyledHero>
      <Background image={image} alt="" />
      <Gradient />
      <BottomFade />

      <TopBar />

      <Badge>
        <C3Mark width={42} height={70} />
        <BadgeTitle>Staalframebouw</BadgeTitle>
      </Badge>

      <Content className="container">
        {title && <TitleTop>{title}</TitleTop>}
        {titleOutline && <TitleBottom>{titleOutline}</TitleBottom>}

        {(link || secondLink) && (
          <Buttons>
            {link && (
              <ButtonLanding to={link.url} target={link.target} variant="red">
                {link.title}
              </ButtonLanding>
            )}
            {secondLink && (
              <ButtonLanding
                to={secondLink.url}
                target={secondLink.target}
                variant="outline"
              >
                {secondLink.title}
              </ButtonLanding>
            )}
          </Buttons>
        )}
      </Content>
    </StyledHero>
  )
}

export default HeroLanding
